/**
 * Brand Configuration Module
 * Defines brand-specific settings, domain generation, and language validation
 *
 * @format
 */

export const BRANDS = {
	wjec: {
		name: "WJEC",
		baseDomain: "resources.wjec.co.uk",
		supportedLanguages: ["en", "cy"] as const,
	},
	eduqas: {
		name: "Eduqas",
		baseDomain: "resources.eduqas.co.uk",
		supportedLanguages: ["en"] as const,
	},
} as const;

export type Brand = keyof typeof BRANDS;
export type Language = "en" | "cy";

export const AWS_REGION = "eu-west-2";

export const VALID_BRANDS = Object.keys(BRANDS) as Brand[];

/**
 * Get the hosted zone name for a brand and environment
 * For prod: resources.wjec.co.uk
 * For non-prod: dev.resources.wjec.co.uk, staging.resources.wjec.co.uk
 */
export const getHostedZoneName = (brand: Brand, environment: string) => {
	const brandConfig = BRANDS[brand];
	const isProd = environment === "prod";

	return isProd ? brandConfig.baseDomain : `${environment}.${brandConfig.baseDomain}`;
};

/**
 * Generate domain names for a brand and environment
 */
export const getDomains = (brand: Brand, environment: string) => {
	const hostedZoneName = getHostedZoneName(brand, environment);

	return {
		hostedZoneName,
		website: `questionbank.${hostedZoneName}`,
		api: `api.questionbank.${hostedZoneName}`,
		assets: `assets.questionbank.${hostedZoneName}`,
	};
};

/**
 * Port used by the Vite dev server for the web package.
 * Kept here so it stays in sync with the CORS allow-list generated below.
 */
export const LOCAL_DEV_PORT = 3000;

/**
 * Origins used when running the web app locally against a deployed environment
 * (e.g. `pnpm dev` served from https://local.<hostedZone>:3000).
 * Returns an empty list for prod so we never widen CORS in production.
 */
export const getLocalDevOrigins = (brand: Brand, environment: string): string[] => {
	if (environment === "prod") {
		return [];
	}
	const hostedZoneName = getHostedZoneName(brand, environment);
	return [`https://local.${hostedZoneName}:${LOCAL_DEV_PORT}`];
};

/**
 * Get complete brand configuration for an environment
 */
export const getBrandConfig = (brand: Brand, environment: string) => ({
	...BRANDS[brand],
	...getDomains(brand, environment),
	indexName: environment === "prod" ? `questions-${brand}` : `questions-${brand}-${environment}`,
	assetsBucket: `question-bank-assets-${brand}-${environment}`,
	websiteBucket: `question-bank-www-${brand}-${environment}`,
});

/**
 * Validate if a language is supported for a brand
 */
export const validateLanguage = (brand: Brand, language: string): boolean => {
	const supportedLanguages = BRANDS[brand].supportedLanguages as readonly string[];
	return supportedLanguages.includes(language);
};

/**
 * Check if brand is valid
 */
export const isValidBrand = (brand: string): brand is Brand => {
	return VALID_BRANDS.includes(brand as Brand);
};

/**
 * Determine the brand from a request/caller hostname by matching against each
 * brand's `baseDomain` (e.g. `local.dev.resources.eduqas.co.uk` -> `eduqas`).
 *
 * Brand is a property of the URL the user is visiting, not their identity, so
 * the same app build serves the correct brand based on the host it's reached on.
 * Falls back to `wjec` when no brand domain matches.
 */
export const getBrandFromHostname = (hostname: string): Brand => {
	const normalised = hostname.toLowerCase();
	const match = VALID_BRANDS.find((brand) => normalised.endsWith(BRANDS[brand].baseDomain));
	return match ?? "wjec";
};

/**
 * Determine the deployment environment from a caller hostname.
 *
 * Prod hosts have no environment prefix (e.g. `questionbank.resources.wjec.co.uk`),
 * whereas non-prod hosts carry a `dev`/`staging` label before the brand's
 * `baseDomain` (e.g. `questionbank.staging.resources.wjec.co.uk`). A single
 * build artifact is deployed to every environment, so this runtime check is how
 * the web app tells them apart. Unknown hosts (e.g. `localhost`) resolve to
 * `dev` so production-only behaviour never leaks into local or preview builds.
 */
export const getEnvironmentFromHostname = (hostname: string): "dev" | "staging" | "prod" => {
	const normalised = hostname.toLowerCase();
	const brand = VALID_BRANDS.find((b) => normalised.endsWith(BRANDS[b].baseDomain));
	if (!brand) {
		return "dev";
	}
	const prefix = normalised.slice(0, normalised.length - BRANDS[brand].baseDomain.length);
	const labels = prefix.split(".").filter(Boolean);
	if (labels.includes("staging")) {
		return "staging";
	}
	if (labels.includes("dev")) {
		return "dev";
	}
	return "prod";
};

/**
 * Environment configuration type
 */
export interface EnvironmentConfig {
	/**
	 * Base URL for the Resources Auth API (used for the authorizer's `/me` lookup).
	 */
	authApiUrl: string;
}

/**
 * Get environment-specific configuration
 */
export const getEnvironmentConfig = (environment: string): EnvironmentConfig => {
	const configs: Record<string, EnvironmentConfig> = {
		dev: {
			authApiUrl: "https://auth-api.dev.resources.wjec.co.uk",
		},
		staging: {
			authApiUrl: "https://auth-api.staging.resources.wjec.co.uk",
		},
		prod: {
			authApiUrl: "https://auth-api.resources.wjec.co.uk",
		},
	};

	return configs[environment] || configs.dev;
};
